import React, { useState, useEffect, useRef } from "react";
import { base44 } from "@/api/base44Client";
import { MessageSquare, X, Send, MinusCircle, Bot, User, Phone } from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

const WHATSAPP_NUMBER = "972523877996";

const WELCOME_MESSAGES = [
  {
    id: "welcome-1",
    role: "bot",
    text: "👋 Hi! I'm Marstick's support assistant. How can I help you today?",
  },
  {
    id: "welcome-2",
    role: "bot",
    text: "You can ask me anything about navigation, weather, safety, boats, or any app features. I'm here to help!",
  },
];

const QUICK_REPLIES = [
  "How do I plan a route?",
  "How do I use the weather forecast?",
  "What is the MOB alert?",
  "How do I add my boat?",
  "Talk to a live agent",
];

export default function ChatBot({ user, compactMapPosition = false }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(WELCOME_MESSAGES);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showQuickReplies, setShowQuickReplies] = useState(true);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const addBotMessage = (text, extra = {}) => {
    setMessages((prev) => [
      ...prev,
      { id: `bot-${Date.now()}`, role: "bot", text, ...extra },
    ]);
  };

  const handleSend = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput("");
    setShowQuickReplies(false);

    // Add user message
    setMessages((prev) => [
      ...prev,
      { id: `user-${Date.now()}`, role: "user", text: msg },
    ]);

    // Check if user wants a live agent
    const liveAgentTriggers = ["live agent", "human", "real person", "talk to someone", "agent", "support person", "whatsapp", "call", "phone"];
    const wantsLiveAgent = liveAgentTriggers.some((t) => msg.toLowerCase().includes(t)) || msg === "Talk to a live agent";

    if (wantsLiveAgent) {
      setTimeout(() => {
        addBotMessage(
          "Sure! I'll connect you with a live agent on WhatsApp right now. Click the button below to start chatting. 🟢",
          { whatsapp: true }
        );
      }, 600);
      return;
    }

    // AI response
    setLoading(true);
    try {
      const response = await Promise.race([
        base44.integrations.Core.InvokeLLM({
          prompt: `You are a friendly maritime support assistant for Marstick, a sailing navigation app. 
Answer the user's question helpfully and concisely. Focus on: navigation, route planning, weather, safety (MAYDAY, MOB, PAN-PAN), marinas, logbook, boat management, fuel optimization, AIS tracking, and app features.
If you don't know something specific, suggest they contact a live agent.
Keep replies short (2-3 sentences max). Be warm and nautical-themed.

User question: "${msg}"`,
        }),
        new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 15000)),
      ]);
      addBotMessage(response);
    } catch {
      addBotMessage("I’m having trouble responding right now. Please try again or connect with a live agent.", { offerAgent: true });
    } finally {
      setLoading(false);
    }

    // After AI reply, offer live agent option
    setTimeout(() => {
      addBotMessage("Still need more help? I can connect you with a live agent. 💬", { offerAgent: true });
    }, 800);
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const openWhatsApp = () => {
    const text = encodeURIComponent("Hi! I need support with Marstick app.");
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${text}`, "_blank");
  };

  return (
    <div className={cn("fixed right-3 z-40 flex flex-col items-end gap-3 md:right-6 md:z-[9999]", compactMapPosition ? "bottom-[calc(10.25rem+env(safe-area-inset-bottom,0px))] md:bottom-24" : "bottom-[calc(8.75rem+env(safe-area-inset-bottom,0px))] md:bottom-6")}>
      {/* Chat Window */}
      {open && (
        <div
          className="w-[calc(100vw-1.5rem)] max-w-80 bg-card border border-primary/30 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          style={{ height: compactMapPosition ? "min(420px, calc(100dvh - 13rem))" : "min(480px, calc(100dvh - 12rem))" }}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-primary to-accent px-4 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <Bot className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="font-bold text-white text-sm">Marstick Support</p>
                <p className="text-white/70 text-[10px] flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" />
                  AI Assistant Online
                </p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/80 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {messages.map((msg) => (
              <div key={msg.id} className={cn("flex gap-2", msg.role === "user" ? "justify-end" : "justify-start")}>
                {msg.role === "bot" && (
                  <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center shrink-0 mt-1">
                    <Bot className="w-3 h-3 text-primary" />
                  </div>
                )}
                <div className="flex flex-col gap-2 max-w-[80%]">
                  <div
                    className={cn(
                      "px-3 py-2 rounded-2xl text-sm leading-relaxed",
                      msg.role === "user"
                        ? "bg-primary text-primary-foreground rounded-br-sm"
                        : "bg-muted text-foreground rounded-bl-sm"
                    )}
                  >
                    {msg.text}
                  </div>

                  {/* WhatsApp CTA */}
                  {msg.whatsapp && (
                    <button
                      onClick={openWhatsApp}
                      className="flex items-center gap-2 px-3 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-semibold transition-colors"
                    >
                      <Phone className="w-4 h-4" />
                      Chat on WhatsApp
                    </button>
                  )}

                  {/* Offer agent button */}
                  {msg.offerAgent && (
                    <button
                      onClick={() => handleSend("Talk to a live agent")}
                      className="flex items-center gap-2 px-3 py-2 bg-card border border-primary/40 hover:bg-primary/10 text-foreground rounded-xl text-sm font-medium transition-colors"
                    >
                      <User className="w-4 h-4 text-primary" />
                      Connect with live agent
                    </button>
                  )}
                </div>
                {msg.role === "user" && (
                  <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center shrink-0 mt-1">
                    <User className="w-3 h-3 text-primary-foreground" />
                  </div>
                )}
              </div>
            ))}

            {/* Typing indicator */}
            {loading && (
              <div className="flex gap-2 justify-start">
                <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                  <Bot className="w-3 h-3 text-primary" />
                </div>
                <div className="bg-muted px-4 py-3 rounded-2xl rounded-bl-sm flex gap-1 items-center">
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}

            {/* Quick Replies */}
            {showQuickReplies && !loading && (
              <div className="flex flex-wrap gap-2 pt-1">
                {QUICK_REPLIES.map((qr) => (
                  <button
                    key={qr}
                    onClick={() => handleSend(qr)}
                    className={cn(
                      "px-3 py-1.5 rounded-full text-xs font-medium border transition-colors",
                      qr === "Talk to a live agent"
                        ? "bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:border-emerald-700 dark:text-emerald-400"
                        : "bg-card border-primary/30 text-foreground hover:bg-primary/10 hover:border-primary/60"
                    )}
                  >
                    {qr}
                  </button>
                ))}
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="p-3 border-t border-border flex gap-2 shrink-0">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Ask me anything..."
              disabled={loading}
              className="flex-1 bg-muted/50 border border-border rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-primary/50 text-foreground placeholder-muted-foreground disabled:opacity-60"
            />
            <button
              onClick={() => handleSend()}
              disabled={!input.trim() || loading}
              className="w-9 h-9 rounded-xl bg-primary hover:bg-primary/90 disabled:opacity-40 flex items-center justify-center transition-colors shrink-0"
            >
              <Send className="w-4 h-4 text-primary-foreground" />
            </button>
          </div>
        </div>
      )}

      {/* Toggle Button */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-14 h-14 rounded-full bg-gradient-to-br from-primary to-accent shadow-lg hover:scale-105 transition-transform flex items-center justify-center"
      >
        {open ? (
          <MinusCircle className="w-6 h-6 text-white" />
        ) : (
          <MessageSquare className="w-6 h-6 text-white" />
        )}
      </button>
    </div>
  );
}